import { memo, useMemo } from 'react';

import LoadingButton from '@zenbusiness/zui/LoadingButton';
import PropTypes from 'prop-types';

import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import useFormStep from '#client/hooks/useFormStep';
import { checkIsMobile } from '#client/utils/devices';

import NeedUpdateCheckAmount from './NeedUpdateCheckAmount';
import NeedUploadBackSideImage from './NeedUploadBackSideImage';
import NeedUploadFrontSideImage from './NeedUploadFrontSideImage';
import OtherReasons from './OtherReasons';

import { DialogContentStyled, TitleStyled, Wrapper } from './styles';
import { RESUBMIT_ACTIONS } from '../constants/actions';
import { REJECT_REASON_LIST } from '../constants/reasons';

const ConfirmationRejectReasons = ({ reason }) => {
  const { dispatchResubmitData } = useFormStep();
  const isDesktop = !checkIsMobile();
  const isShowBanner = reason !== REJECT_REASON_LIST.UPDATE_CHECK_AMOUNT && isDesktop;

  const contentByRejectReason = useMemo(() => {
    const title = 'There was an error submitting your check deposit.';
    const retry = () => {
      dispatchResubmitData({ type: RESUBMIT_ACTIONS.GO_TO_RESUBMIT });
    };

    switch (reason) {
      case REJECT_REASON_LIST.UPDATE_CHECK_AMOUNT:
        return {
          title,
          content: <NeedUpdateCheckAmount />,
          retry
        };

      case REJECT_REASON_LIST.UPLOAD_FRONT_SIDE_IMAGE:
        return {
          title,
          content: <NeedUploadFrontSideImage isShowBanner={isShowBanner} />,
          retry
        };

      case REJECT_REASON_LIST.UPLOAD_BACK_SIDE_IMAGE:
      case REJECT_REASON_LIST.UPLOAD_BACK_SIDE_IMAGE_ONLY_MOBILE:
        return {
          title,
          content: (
            <NeedUploadBackSideImage
              forMobileOnly={reason === REJECT_REASON_LIST.UPLOAD_BACK_SIDE_IMAGE_ONLY_MOBILE}
              isShowBanner={isShowBanner}
            />
          ),
          retry
        };

      default:
        return {
          title,
          content: <OtherReasons isShowBanner={isShowBanner} />,
          retry
        };
    }
  }, [dispatchResubmitData, isShowBanner, reason]);
  // TODO: we will hide the Cancel for now, need handle late
  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper>
              <TitleStyled
                size="2xl"
                weight="bold"
              >
                {contentByRejectReason.title}
              </TitleStyled>
              {contentByRejectReason.content}
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      {!isShowBanner && (
        <ActionButtonsGrid>
          <LoadingButton
            aria-label="retry"
            color="primary"
            disabled={false}
            fullWidth
            onClick={contentByRejectReason.retry}
            size="large"
            variant="contained"
          >
            Retry
          </LoadingButton>
        </ActionButtonsGrid>
      )}
    </>
  );
};

ConfirmationRejectReasons.propTypes = {
  reason: PropTypes.string
};

export default memo(ConfirmationRejectReasons);
